import { PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Alert, Button, Checkbox, Col, DatePicker, Form, Input, Radio, Row, Select, Space, Tooltip, message } from 'antd';
import { getCustomerContacts } from 'api/coreapi/customers';
import { getCitiesWithCoverage } from 'api/coreapi/location';
import { createOrder } from 'api/coreapi/orders';
import OrderRequirementSelect from 'components/orderRequirementSelect';
import PaymentMethodSelector from 'components/paymentMethodSelector';
import ErrorMessage from 'components/errorMessage';
import ServiceTypesSelect from 'components/serviceTypesSelect';
import TruckTypeSelect from 'components/truckTypeSelect';
import { ServiceTypes } from 'constants/services';
import { PaymentTypes } from 'constants/payments';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import ContactSelector from 'features/contacts/contactSelector';
import InternalCustomerSelector from 'features/customers/internalCustomerSelector';
import QuickCreateInternalCustomerModal from 'features/customers/quickCreateInternalCustomerModal';
import { useAppSelector } from 'hooks/useAppSelector';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PATHS from 'routes/paths';
import { City } from 'shared/models/cityModel';
import { Contact } from 'shared/models/contactModel';
import { CustomerOverview } from 'shared/models/customerOverviewModel';
import { Order } from 'shared/models/orderModel';
import { UpsertOrderPayload } from 'shared/models/payloads/upsertOrderPayload';
import { getDuration, getDurationAsHours } from 'utils/dateUtils';
dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.locale('es');
const { TextArea } = Input;
const customerOrderReferencePattern = new RegExp('^[\\p{L}\\p{N} ._/#-]+$', 'u');
const generalAdditionalInformationDescription = "Indicaciones puntuales del servicio. Por ejemplo especificar si la mercancía va para un piso 3, 4 o 5, si es estibada, si requiere separación y etiquetado,si requiere revisión previa al cargue y/o descargue, si el desplazamiento es superior a 30 mts, si es contenedor de 20' o de 40'  etc";
const logisticSupportAdditionalInformationDescription = "Breve descripción de las actividades o funciones a realizar.";
const logisticSupportMinimumHours = 7;

export type OrderFormContext = 'customer' | 'administrative';

interface OrderFormProps {
    context: OrderFormContext;
    order?: Order;
    initialCustomer?: CustomerOverview;
}

export const OrderForm = (props: OrderFormProps) => {
    const [selectedServiceType, setSelectedServiceType] = useState<string>();
    const [cities, setCities] = useState<City[]>();
    const [loading, setLoading] = useState(false);
    const [isNewOrder] = useState(props.order?.id === undefined);
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [selectedStartDate, setSelectedStartDate] = useState(props.order?.startDate);
    const [selectedEndDate, setSelectedEndDate] = useState(props.order?.endDate);
    const [contacts, setContacts] = useState<Contact[]>();
    const currentUser = useAppSelector(state => state.currentUser);
    const [endDateValidationMessage, setEndDateValidationMessage] = useState<string>();
    const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerOverview | undefined>(props.initialCustomer);
    const [quickCreateOpen, setQuickCreateOpen] = useState(false);
    const isAdministrative = props.context === 'administrative';

    const initialValues = {
        ...props.order,
        serviceTypeId: props.order?.serviceTypeId?.toString(),
        startDate: props.order?.startDate ? dayjs(props.order.startDate) : null,
        endDate: props.order?.endDate ? dayjs(props.order.endDate) : null,
        shift: props.order?.shiftStartTime ? [dayjs(props.order.shiftStartTime, 'HH:mm:ss'), dayjs(props.order.shiftEndTime, 'HH:mm:ss')] : null,
        requirementIds: props.order?.requirements?.map(item => { return item.id.toString() }),
        paymentTypeId: isNewOrder
            ? isAdministrative ? PaymentTypes.Credit : PaymentTypes.Transfer
            : props.order?.paymentTypeId,
        isLooseCargo: isNewOrder ? "true" : props.order?.isLooseCargo ? "true" : "false"
    }

    useEffect(() => {
        if (props.order?.serviceTypeId) {
            setSelectedServiceType(props.order.serviceTypeId.toString());
        }

        const getInfo = async () => {
            try {
                setLoading(true);
                const citiesResult = await getCitiesWithCoverage();
                setCities(citiesResult.result);
            } catch (error: any) {
                message.error('Error obteniendo información.');
            } finally {
                setLoading(false);
            }
        };
        const getContacts = async () => {
            const customerId = isAdministrative ? selectedCustomer?.id : currentUser?.customer?.id;
            if (!customerId || !isNewOrder) {
                if (isAdministrative) {
                    setContacts([]);
                    setSelectedContactIds([]);
                }
                return;
            }

            try {
                const response = await getCustomerContacts(customerId);
                setContacts(response.result);
                setSelectedContactIds([]);
            } catch (error: any) {
                setContacts([]);
                message.error('Error obteniendo los contactos del cliente.');
            }
        };

        getContacts();
        getInfo();

    }, [props.order, currentUser, isAdministrative, isNewOrder, selectedCustomer?.id]);
    const onFinish = async (values: any) => {
        if (isAdministrative && !selectedCustomer) {
            message.error('Debe seleccionar un cliente para la orden.');
            return;
        }

        if (!isAdministrative && selectedContactIds.length <= 0) {
            message.error('Debe seleccionar al menos una persona como contacto para este servicio.');
            return;
        }

        let payload = values as UpsertOrderPayload;
        payload = {
            ...values,
            customerId: isAdministrative ? selectedCustomer?.id : undefined,
            customerOrderReference: values.customerOrderReference?.trim() || undefined,
            contactIds: selectedContactIds,
            endDate: dayjs(selectedEndDate),
            shiftStartTime: values.shift ? values.shift[0].format('HH:mm') : null,
            shiftEndTime: values.shift ? values.shift[1].format('HH:mm') : null,
            isLooseCargo: values.isLooseCargo === "true",
        };
        try {
            setLoading(true);
            message.info('Guardando solicitud de servicio...');
            var result = await createOrder(payload);

            if (result.result?.id) {
                message.success('Información actualizada correctamente.');

                if (isNewOrder) {
                    navigate(PATHS.ORDER_DETAIL.replace(':orderNumber', result.result.orderNumber.toString()));
                }
            }
        } catch (error: any) {
            if (Array.isArray(error.errors)) {
                message.error(ErrorMessage(error.errors));
            }
            else {
                message.error('Ha ocurrido un error.');
            }
        }
        finally {
            setLoading(false);
        }
    };

    const onStartDateChange = (date: any) => {
        setSelectedStartDate(date);

        if (date) {
            const defaultDurationInHours = selectedServiceType === ServiceTypes.LogisticsAssistant.toString() ? logisticSupportMinimumHours : 4;
            const endDate = dayjs(date).add(defaultDurationInHours, 'hours');
            setSelectedEndDate(endDate.toDate());
            form.setFieldsValue({ endDate });
            validateOrderDuration(date, endDate);
        }
    };

    const onEndDateChange = (date: any) => {
        if (!date)
            return;
        setSelectedEndDate(date);
        validateOrderDuration(selectedStartDate, date);

    };

    const validateOrderDuration = (startDate?: any, endDate?: any) => {
        let startDateToValidate = startDate ?? selectedStartDate;
        let endDateToValidate = endDate ?? selectedEndDate;

        if (!startDate || !endDate) {
            setEndDateValidationMessage(undefined);
            return;
        }

        let duration = getDurationAsHours(startDateToValidate, endDateToValidate);

        if (!duration || isNaN(duration) || duration > 12) {
            setEndDateValidationMessage("La duración máxima permitida es 12 horas.");
        }
        else {
            setEndDateValidationMessage(undefined);
        }
    }

    return (
        <Form onFinish={onFinish} form={form} initialValues={initialValues} disabled={!isNewOrder}
            labelWrap labelCol={{ span: 8 }}
            wrapperCol={{ span: 14 }}>
            {isNewOrder && isAdministrative && (
                <Form.Item label="Cliente" required>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <InternalCustomerSelector value={selectedCustomer} onChange={setSelectedCustomer} />
                        <Button icon={<PlusOutlined />} onClick={() => setQuickCreateOpen(true)}>
                            Crear cliente rápido
                        </Button>
                    </Space>
                </Form.Item>
            )}
            <Form.Item
                label="Referencia de la orden del cliente"
                name="customerOrderReference"
                extra="Código opcional asignado por el cliente. Ejemplo: OC-2026-001."
                rules={[{
                    validator: (_, value?: string) => {
                        const normalizedValue = value?.trim();
                        if (!normalizedValue) {
                            return Promise.resolve();
                        }
                        if (normalizedValue.length > 100) {
                            return Promise.reject(new Error('La referencia no puede superar los 100 caracteres.'));
                        }
                        if (!customerOrderReferencePattern.test(normalizedValue)) {
                            return Promise.reject(new Error('Use letras, números, espacios o los caracteres - / . _ #.'));
                        }
                        return Promise.resolve();
                    },
                }]}
            >
                <Input maxLength={100} showCount placeholder="OC-2026-001" />
            </Form.Item>
            <Form.Item label="Forma de pago" name="paymentTypeId" rules={[{ required: true }]}>
                <PaymentMethodSelector onSelected={(paymentTypeId: number) => form.setFieldValue('paymentTypeId', paymentTypeId)}
                    availablePaymentTypes={isAdministrative
                        ? [PaymentTypes.Credit, PaymentTypes.Cash]
                        : [PaymentTypes.Cash, PaymentTypes.Transfer]}
                    disabled={!isNewOrder} ></PaymentMethodSelector>
            </Form.Item>
            <Form.Item label="Tipo servicio" name="serviceTypeId" rules={[{ required: true }]}>
                <ServiceTypesSelect onChange={(item) => (setSelectedServiceType(item?.toString()))}></ServiceTypesSelect>
            </Form.Item>
            <Form.Item label="Fecha y hora del servicio" name="startDate" rules={[{ required: true }]}>
                <DatePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm"
                    onChange={onStartDateChange} autoComplete="off" />
            </Form.Item>


            <Form.Item label="Fecha estimada finalización" name="endDate" validateStatus={endDateValidationMessage ? 'error' : 'success'}
                help={endDateValidationMessage}
                rules={[{ required: true }]}>
                <>
                    <Row>
                        <Col xxl={{ span: 8 }} lg={{ span: 14 }} md={{ span: 19 }} sm={{ span: 24 }}>
                            <DatePicker value={(selectedEndDate ? dayjs(selectedEndDate) : null)} showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm"
                                onChange={onEndDateChange} autoComplete="off" />
                        </Col>
                        <Col xxl={{ span: 8 }} lg={{ span: 10 }} md={{ span: 5 }} sm={{ span: 20 }}>
                            <span>Duración estimada: {getDuration(selectedStartDate, selectedEndDate)}</span>
                        </Col>
                    </Row>
                    {isNewOrder && selectedServiceType === ServiceTypes.LogisticsAssistant.toString() &&
                        selectedEndDate && selectedStartDate &&
                        (getDurationAsHours(selectedStartDate, selectedEndDate) ?? 0) < logisticSupportMinimumHours &&
                        <Row>
                            <Alert message={`Se aplicará cargo mínimo de ${logisticSupportMinimumHours} horas por auxiliar.`} type='warning'
                                style={{ marginTop: 20 }}></Alert>
                        </Row>
                    }
                </>
            </Form.Item>


            <Form.Item label="Ciudad" name="cityId" rules={[{ required: true }]}>
                <Select options={cities?.map((item) => ({ label: item.displayName, value: item.id }))} />
            </Form.Item>
            <Form.Item label="Dirección del servicio" name="addressLine1" rules={[{ required: true }]}>
                <Input />
            </Form.Item>
            <Form.Item label="Requerimientos especiales" name="requirementIds">
                <OrderRequirementSelect mode='multiple'></OrderRequirementSelect>
            </Form.Item>
            <Form.Item label="# de hombres" name="peopleAmount"
                rules={[
                    { min: 1 },
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            let convertedValue = Number(value);

                            if (isNaN(convertedValue)) {
                                return Promise.reject(new Error("Debe ingresar un número válido."));
                            }

                            return Promise.resolve();
                        },
                    }),
                    () => ({
                        validator(_, value) {
                            if (isNaN(value) || value < 1) {
                                return Promise.reject(new Error("Debe solicitar mínimo una persona."));
                            }

                            return Promise.resolve();
                        },
                    }),]}>
                <Input type='number'></Input>
            </Form.Item>

            {(selectedServiceType === '1' || selectedServiceType === '2') && (
                <>
                    <Form.Item name="isLooseCargo" label="Es carga suelta?"
                        rules={[{ required: true, message: 'Por favor indique si la mercancía es carga suelta o estibada.' }]}>
                        <Radio.Group optionType="button" buttonStyle="solid">
                            <Radio.Button value="true">Carga suelta</Radio.Button>
                            <Radio.Button value="false">Estibada</Radio.Button>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item name="requiresUpperFloorHandling" valuePropName='checked' label="Requiere desplazamiento a nivel(piso) superior."
                        tooltip="Marque esta casilla en caso de que requiera cargar o descargar mercancía desde un piso superior al primero.">
                        <Checkbox></Checkbox>
                    </Form.Item>

                    <Form.Item label="Mercancía" name="loadType" rules={[{ required: true, max: 100 }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item label="Empaque" name="packageType" rules={[{ required: true, max: 100 }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item label="Peso (Toneladas)" name="weight" rules={[{ required: true }]}>
                        <Input type="number" />
                    </Form.Item>
                    <Form.Item label="Unidades" name="packagingUnits" rules={[{ required: false }]}>
                        <Input type="number" />
                    </Form.Item>
                    <Form.Item label="Configuración vehículo" name="truckTypeId" rules={[{ required: true }]}>
                        <TruckTypeSelect />
                    </Form.Item>

                </>
            )}
            {/* {(selectedServiceType === '3' || selectedServiceType === '4') && (
                <>
                    <Form.Item label="Turno" name="shift" rules={[{ required: true }]}>
                        <TimePicker.RangePicker order={false} format='HH:mm' />
                    </Form.Item>
                </>
            )} */}
            <Form.Item
                label={
                    <Space>
                        Información complementaria
                        <Tooltip title={selectedServiceType === ServiceTypes.LogisticsAssistant.toString()
                            ? logisticSupportAdditionalInformationDescription
                            : generalAdditionalInformationDescription}>
                            <QuestionCircleOutlined />
                        </Tooltip>
                    </Space>}
                name="comments">
                <TextArea />
            </Form.Item>
            {isNewOrder && <>

                {contacts &&
                    <Row justify="center">
                        <Col>
                            {isAdministrative && <Alert
                                message="Contacto opcional"
                                description="La selección de contactos es opcional para una orden administrativa."
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }} />}
                            <ContactSelector
                                contacts={contacts ?? []}
                                onChange={setSelectedContactIds}
                                canEdit={true}
                                label="Personas de contacto para este servicio"
                                empty={isAdministrative ? <Alert
                                    message="Sin contactos registrados"
                                    description="Puede crear la orden sin contactos para este cliente."
                                    type="info"
                                    showIcon /> : <Alert
                                    message="¡Atención!"
                                    description={
                                        <div>
                                            Para seleccionar los contactos que prestarán apoyo en el servicio, por favor registralos <Link to={PATHS.CONTACTS}>aquí</Link>.<br />
                                            Ingresa los detalles de los contactos para avanzar con la asignación del servicio.
                                            <br />
                                            <br />
                                            ¡Gracias por tu colaboración!
                                        </div>
                                    }
                                    type="warning"
                                />}></ContactSelector></Col>
                    </Row>
                }

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading}>{isNewOrder ? 'Crear solicitud' : 'Guardar'}</Button>
                </Form.Item>
            </>
            }

            <QuickCreateInternalCustomerModal
                open={quickCreateOpen}
                onCancel={() => setQuickCreateOpen(false)}
                onCreated={(customer) => {
                    setSelectedCustomer(customer);
                    setQuickCreateOpen(false);
                }} />

        </Form>
    );
};
